import { useTranslation } from '@msdk/common'
import {
  ControlValidationType,
  Entity,
  FormOpenMode,
  ListFieldDefinition,
  ListValue,
  MetadataSelectorDefinition,
  StringValue,
} from '@msdk/definitions'
import {
  BaseSelect,
  ButtonIcon,
  CloseIcon,
  FormField,
  FormFieldLabel,
  OptionItem,
  SearchIcon,
  SelectInputAdornment,
  selectValueRenderer,
} from '@msdk/ui'
import React from 'react'

import { useComponentModal, useFieldSeverity, useFieldValue, useHasMandatoryValidation } from '../../../hooks'
import { FieldProps } from '../../../models/form'
import { nodeHasFlag } from '../../../utils'
import { ValidationMessages } from '../../ValidationMessages'
import { FieldEntityPair } from './field-entity-pair'
import { NodeGroup, NodeItem } from './node'
import { SelectorPanel } from './SelectorPanel'

type Props = {
  nodes: NodeItem[]
  keepSelection?: boolean
} & FieldProps

function findNodeItemInNodes(nodes: NodeItem[], pair: FieldEntityPair): NodeItem | undefined {
  for (const node of nodes) {
    if (node instanceof NodeGroup) {
      const found = findNodeItemInNodes(node.children, pair)
      if (found) {
        return found
      }
    } else {
      if (node.equals(pair)) {
        return node
      }
    }
  }
  return undefined
}

function mapPairsToNodeStrings(nodes: NodeItem[], pairs: FieldEntityPair[]): string[] {
  const result: NodeItem[] = []

  for (const pair of pairs) {
    const node = findNodeItemInNodes(nodes, pair)
    if (node) {
      result.push(node)
    }
  }

  return result.map((n) => n.toString())
}

const mapValues = (pairs: FieldEntityPair[], nodes: NodeItem[]): OptionItem[] => {
  const selected = mapPairsToNodeStrings(nodes, pairs ?? [])
  return selected.map((s) => ({
    value: s,
    label: s,
  }))
}

export function MetadataSelectorComponent({ nodes, node, onChange, fieldArgs, keepSelection }: Props) {
  const { createModal } = useComponentModal()
  const t = useTranslation()
  const fieldDef = node.value.fieldDefinition as ListFieldDefinition
  const formDef = node.value.formDefinition as MetadataSelectorDefinition
  const hasMandatory = useHasMandatoryValidation(node, fieldDef.entityName)
  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)
  const [value, setValue] = useFieldValue(node, onChange)

  const onSearchClick = async () => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const selectedItems = getSelectedItems()
    const modalRef = createModal(
      <SelectorPanel
        nodes={nodes}
        selectedItems={selectedItems}
        multiselect={formDef.multiSelection}
        idml={formDef.modalIdml}
        description={formDef.modalDescription}
      />,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      onPanelDismiss,
    )
    modalRef.open()
  }

  const onClear = () => {
    node.value.isDirty = true
    setValue(null)
  }

  const onPanelDismiss = (selectedItems: FieldEntityPair[]) => {
    if (selectedItems) {
      const rows = selectedItems.map((item) => {
        const row: Entity = {
          entityName: fieldDef.entityName,
          id: crypto.randomUUID(),
          isPersisted: false,
          isInCreation: false,
          parentId: node.value.entityId as string,
          fields: {},
          canUpdate: false,
          canActivate: false,
          canDeactivate: false,
          canDelete: false,
        }
        const entityField = new StringValue(item.entityName)
        const metadataField = new StringValue(item.fieldName)
        row.fields[formDef.entityNameField] = entityField
        row.fields[formDef.metadataField] = metadataField

        return row
      })
      let listValue: ListValue
      if (keepSelection && value?.value && Array.isArray(value.value)) {
        const currentListValue = new ListValue()
        const currentList = value?.value || []
        const checkingList: Entity[] = []

        /**
         * We Create a map of entityNames from currentList to ensure a fast lookup when dealing with a large selected list (only performance optimization).
         *  */
        const currentListMap = new Map(
          currentList.map((item) => [item.fields[formDef.metadataField]?.value as string, item]),
        )

        /**
         * We iterate through rows and find discrepancies between rows and currentList.
         * If the item has been removed from the selection, it won't be taken into consideration in the construction of the new list.
         *  */
        rows.forEach((row) => {
          const rowMetadataField = row.fields[formDef.metadataField]?.value as string

          if (currentListMap.has(rowMetadataField)) {
            /**
             * If metadataField appears also in currentListMap, push the value.value entry to checkingList.
             * This is the case in which the item has been kept selected.
             */
            checkingList.push(currentListMap.get(rowMetadataField)!)
          } else {
            /**
             * If metadatafield appears in rows, but not in value.value, push the row entry to checkingList.
             * This is the case in which the item has been added to the list.
             */
            checkingList.push(row)
          }
        })

        currentListValue.value = checkingList
        listValue = currentListValue
      } else {
        listValue = new ListValue()
        listValue.value = rows
      }
      node.value.isDirty = true
      setValue(listValue)
    }
  }

  const getSelectedItems = () => {
    const res: FieldEntityPair[] = []
    if (value) {
      const listValue = value as ListValue
      listValue.value?.forEach((row) => {
        const entityName = row.fields[formDef.entityNameField]?.value as string
        const fieldName = row.fields[formDef.metadataField]?.value as string
        const pair = new FieldEntityPair(fieldName, entityName)
        res.push(pair)
      })
    }
    return res
  }

  const selectedItems = getSelectedItems()
  const hasValue = selectedItems.length > 0
  const isReadOnly = node.value.state === 'Readonly' || fieldArgs?.formOpenMode === FormOpenMode.Readonly
  const severity = useFieldSeverity(fieldDef, node)

  return (
    <FormField
      required={(hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly}
      size="small"
      severity={severity}
      readOnly={isReadOnly}
      disabled={isReadOnly}
    >
      {fieldArgs?.showLabel && (
        <FormFieldLabel shrink={hasValue}>{t(formDef.overrideIDML, formDef.overrideDescription)}</FormFieldLabel>
      )}
      <BaseSelect
        open={false}
        label={fieldArgs?.showLabel && hasValue ? t(formDef.overrideIDML, formDef.overrideDescription) : undefined}
        disabled
        multiple
        value={selectedItems}
        IconComponent={() => null}
        renderValue={(a) => selectValueRenderer(mapValues(a as FieldEntityPair[], nodes))}
        endAdornment={
          <SelectInputAdornment>
            {!isReadOnly && (keepSelection || !value || value?.isEmpty()) && (
              <ButtonIcon color="primary" aria-label="Upload" onClick={onSearchClick} edge="end">
                <SearchIcon />
              </ButtonIcon>
            )}
            {!isReadOnly && value && !value?.isEmpty() && (
              <ButtonIcon color="primary" aria-label="Upload" onClick={onClear} edge="end">
                <CloseIcon />
              </ButtonIcon>
            )}
          </SelectInputAdornment>
        }
      />
      <ValidationMessages messages={node.value.validationMessages} fieldDef={fieldDef} node={node} />
    </FormField>
  )
}
